import React, { useState } from 'react';

import { ScreenHead } from '../../ui/screen-parts.js';
import { Chip, Kpis, Panel, Segments, Empty, useFlash } from '../../ui/kit.js';
import { day, nextId } from '../../state/building.js';

import Bulbs from '../../media/images/modules/stock-bulbs.jpg';
import Chandelier from '../../media/images/modules/stock-chandelier.jpg';
import Filters from '../../media/images/modules/stock-filters.jpg';
import PoolChemicals from '../../media/images/modules/stock-pool-chemicals.jpg';
import Cleaning from '../../media/images/modules/stock-cleaning.jpg';
import Fobs from '../../media/images/modules/stock-fobs.jpg';
import Towels from '../../media/images/modules/stock-towels.jpg';

const CATEGORIES = ['All', 'Electrical', 'HVAC', 'Pool', 'Janitorial', 'Access', 'Linens'];

const SEED = [
    { id: 1, photo: Bulbs, name: 'LED A19 bulbs, 2700K', category: 'Electrical', onHand: 38, par: 60, unit: 'bulbs', location: 'P1 shop, shelf 2' },
    { id: 2, photo: Chandelier, name: 'Lobby chandelier candelabra bulbs', category: 'Electrical', onHand: 6, par: 40, unit: 'bulbs', location: 'P1 shop, shelf 2' },
    { id: 3, photo: Filters, name: 'MERV 13 filters, 20x25x4', category: 'HVAC', onHand: 22, par: 48, unit: 'filters', location: 'Roof mechanical' },
    { id: 4, photo: Filters, name: 'Fan coil filters, 16x20x1', category: 'HVAC', onHand: 140, par: 200, unit: 'filters', location: 'P1 shop, bay 3' },
    { id: 5, photo: PoolChemicals, name: 'Liquid chlorine', category: 'Pool', onHand: 3, par: 20, unit: 'gallons', location: 'Pool equipment room' },
    { id: 6, photo: PoolChemicals, name: 'Muriatic acid', category: 'Pool', onHand: 7, par: 12, unit: 'gallons', location: 'Pool equipment room' },
    { id: 7, photo: Cleaning, name: 'Marble floor cleaner', category: 'Janitorial', onHand: 9, par: 16, unit: 'jugs', location: 'Housekeeping closet' },
    { id: 8, photo: Cleaning, name: 'Glass cleaner concentrate', category: 'Janitorial', onHand: 14, par: 18, unit: 'bottles', location: 'Housekeeping closet' },
    { id: 9, photo: Fobs, name: 'Spare resident key fobs', category: 'Access', onHand: 11, par: 50, unit: 'fobs', location: 'Security office safe' },
    { id: 10, photo: Towels, name: 'Pool towels, cabana stripe', category: 'Linens', onHand: 180, par: 240, unit: 'towels', location: 'Pool deck linen room' },
    { id: 11, photo: Towels, name: 'Gym hand towels', category: 'Linens', onHand: 45, par: 150, unit: 'towels', location: 'Fitness center' }
];

const level = item => Math.round((item.onHand / item.par) * 100);
const tone = pct => (pct < 25 ? 'alert' : pct < 50 ? 'warn' : 'ok');

/* Inventory Management: supplies on hand against par, adjusted as they
   are used, and a reorder list for what runs short. */
function InventoryScreen() {
    const [items, setItems] = useState(SEED);
    const [orders, setOrders] = useState([
        { id: 1, itemId: 5, name: 'Liquid chlorine', qty: 20, unit: 'gallons', placed: day(-1) }
    ]);
    const [category, setCategory] = useState('All');
    const [flash, notify] = useFlash();

    const adjust = (item, delta) => {
        setItems(list => list.map(i => (i.id === item.id ? { ...i, onHand: Math.max(0, i.onHand + delta) } : i)));
    };

    const reorder = item => {
        const qty = Math.max(item.par - item.onHand, 1);
        setOrders(list => [{ id: nextId(), itemId: item.id, name: item.name, qty, unit: item.unit, placed: day(0) }, ...list]);
        notify(`${qty} ${item.unit} of ${item.name} added to the reorder list.`);
    };

    const receive = order => {
        setItems(list => list.map(i => (i.id === order.itemId ? { ...i, onHand: i.onHand + order.qty } : i)));
        setOrders(list => list.filter(o => o.id !== order.id));
        notify(`${order.qty} ${order.unit} of ${order.name} received and shelved.`);
    };

    const shown = items.filter(i => category === 'All' || i.category === category);
    const low = items.filter(i => level(i) < 50);
    const critical = items.filter(i => level(i) < 25);
    const onOrder = id => orders.some(o => o.itemId === id);

    return (
        <div className="screen-content">
            <ScreenHead eyebrow="Inventory" title="Inventory and supplies" />

            <Kpis items={[
                { label: 'Items tracked', value: items.length, tone: 'info' },
                { label: 'Low stock', value: low.length, tone: low.length ? 'warn' : 'ok', note: 'Below half of par' },
                { label: 'Critical', value: critical.length, tone: critical.length ? 'alert' : 'ok', note: 'Below a quarter of par' },
                { label: 'On order', value: orders.length }
            ]} />

            {flash}

            <Segments label="Category" options={CATEGORIES} value={category} onChange={setCategory} />

            <div className="split section-gap">
                <Panel className="panel--accent">
                    <h4>Reorder list</h4>
                    {orders.length === 0 ? <Empty>Nothing on order.</Empty> : (
                        <ul className="rows">
                            {orders.map(order => (
                                <li className="row" key={order.id}>
                                    <div className="row__main">
                                        <span className="row__title">{order.name}</span>
                                        <span className="row__meta">{order.qty} {order.unit} · placed {order.placed}</span>
                                    </div>
                                    <div className="row__end">
                                        <button type="button" className="btn btn-outline-light btn-sm" onClick={() => receive(order)}>Received</button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </Panel>

                <ul className="rows">
                    {shown.map(item => {
                        const pct = level(item);
                        return (
                            <li className="row row--thumb" key={item.id}>
                                <img className="row__thumb" src={item.photo} alt={item.name} loading="lazy" />
                                <div className="row__main">
                                    <span className="row__title">{item.name}</span>
                                    <span className="row__meta">
                                        {item.onHand} of {item.par} {item.unit} · {item.location}
                                    </span>
                                    <div className={`meter${pct < 25 ? ' meter--alert' : pct < 50 ? ' meter--warn' : ''}`}>
                                        <span className="meter__fill" style={{ width: `${Math.min(pct, 100)}%` }} />
                                    </div>
                                </div>
                                <div className="row__end">
                                    <Chip tone={tone(pct)}>{pct}%</Chip>
                                    <button type="button" className="btn btn-outline-light btn-sm" aria-label={`Use one ${item.name}`} onClick={() => adjust(item, -1)}>−</button>
                                    <button type="button" className="btn btn-outline-light btn-sm" aria-label={`Add one ${item.name}`} onClick={() => adjust(item, 1)}>+</button>
                                    {onOrder(item.id) ? <Chip tone="info">On order</Chip> : (
                                        <button type="button" className="btn btn-primary btn-sm" onClick={() => reorder(item)}>Reorder</button>
                                    )}
                                </div>
                            </li>
                        );
                    })}
                </ul>
            </div>
        </div>
    );
}

export default InventoryScreen;
